import { useEffect, useRef, useState, type ReactNode } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import IconButton from '@mui/material/IconButton'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { type ISpRewardBrand } from '@brushfire/types'
import { isMinRedeemBypassed, minRedeemUsd, resolveEffectiveMinUsd } from '../lib/rewards-config'
import { ConfirmationModalShell } from '@/shared/components/confirmation'
import { RiCloseLine, RiInformationLine } from '@/shared/icons/remix'
import {
  createRedeemAmountSchema,
  normalizeRedeemAmountInput,
  type RedeemAmountFormData,
} from '../lib/redeem-amount-schema'
import { buildVariableAmountHintText, isFixedRedemptionAmount } from '../lib/redeem-amount-hint'
import { formatUsd } from '../lib/profileStepUtils'
import { REDEEM_RATE_LIMIT_PREFIX } from '../lib/rewards-copy'
import {
  confirmRewardAmountErrorSx,
  confirmRewardAmountFieldSx,
  confirmRewardAmountHintTextSx,
  confirmRewardAmountLabelSx,
  confirmRewardBalanceRowSx,
  confirmRewardBalanceTextSx,
  confirmRewardBrandLogoPlaceholderSx,
  confirmRewardBrandLogoSx,
  confirmRewardBrandNameSx,
  confirmRewardBrandStripSx,
  confirmRewardCancelButtonSx,
  confirmRewardModalBackdropSx,
  confirmRewardModalCloseButtonSx,
  confirmRewardModalContentSx,
  confirmRewardModalHeaderSx,
  confirmRewardModalPaperSx,
  confirmRewardModalTitleSx,
  confirmRewardRedeemButtonSx,
} from '../styles/confirmRewardModalStyles'

interface Props {
  open: boolean
  brand: ISpRewardBrand | null
  balanceUsd: number
  isSubmitting: boolean
  /** TC-12A-48 — network/API failure copy shown inline while modal stays open. */
  submissionError?: string | null
  /** TC-12B-51: unix ms timestamp when rate limit expires (null = not rate-limited). */
  rateLimitExpiresAt?: number | null
  onRateLimitExpired?: () => void
  onClose: () => void
  onRedeem: (amountUsd: number) => void
}

function formatCountdown(remainingMs: number): string {
  const totalSeconds = Math.max(0, Math.ceil(remainingMs / 1000))
  const mins = Math.floor(totalSeconds / 60)
  const secs = totalSeconds % 60
  return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`
}

function buildFixedAmountHint(brand: ISpRewardBrand): string | null {
  if (!brand.allowedAmountsUsd || brand.allowedAmountsUsd.length <= 1) {
    return null
  }
  return `Allowed amounts: ${brand.allowedAmountsUsd.map((a) => formatUsd(a)).join(', ')}`
}

function renderAmountHint(params: {
  amountError?: string
  isFixedAmount: boolean
  brand: ISpRewardBrand
  fixedAmountHint: string | null
}): ReactNode {
  if (params.amountError) {
    return (
      <Typography
        component="span"
        role="alert"
        sx={confirmRewardAmountErrorSx}
        data-testid="rewards-confirm-amount-error"
      >
        {params.amountError}
      </Typography>
    )
  }
  if (params.isFixedAmount) {
    return (
      <Typography sx={confirmRewardAmountHintTextSx}>
        Fixed redemption amount: {formatUsd(params.brand.minAmountUsd)}
      </Typography>
    )
  }
  if (params.fixedAmountHint) {
    return (
      <Typography sx={confirmRewardAmountHintTextSx}>{params.fixedAmountHint}</Typography>
    )
  }
  return (
    <Typography
      sx={confirmRewardAmountHintTextSx}
      data-testid="rewards-confirm-amount-hint"
    >
      {buildVariableAmountHintText(params.brand)}
    </Typography>
  )
}

export function ConfirmRewardModal({
  open,
  brand,
  balanceUsd,
  isSubmitting,
  submissionError,
  rateLimitExpiresAt,
  onRateLimitExpired,
  onClose,
  onRedeem,
}: Readonly<Props>) {
  const brandMinAmountUsd = brand?.minAmountUsd ?? (isMinRedeemBypassed ? 0 : minRedeemUsd)
  const brandMaxAmountUsd = brand?.maxAmountUsd ?? balanceUsd
  const isFixedAmount = brand !== null && isFixedRedemptionAmount(brand)
  const schema = createRedeemAmountSchema(
    balanceUsd,
    brandMinAmountUsd,
    brandMaxAmountUsd,
    brand?.allowedAmountsUsd,
  )
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    trigger,
    formState: { errors, isValid },
    reset,
  } = useForm<RedeemAmountFormData>({
    resolver: zodResolver(schema),
    mode: 'onChange',
    defaultValues: { amountInput: '' },
  })

  const amountInput = watch('amountInput')

  // ── Rate-limit countdown (TC-12B-51 to TC-12B-55) ────────────────────────
  const [remainingMs, setRemainingMs] = useState<number>(0)
  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null)

  useEffect(() => {
    if (countdownRef.current) {
      clearInterval(countdownRef.current)
      countdownRef.current = null
    }

    if (!rateLimitExpiresAt) {
      setRemainingMs(0)
      return
    }

    const tick = () => {
      const diff = rateLimitExpiresAt - Date.now()
      if (diff <= 0) {
        setRemainingMs(0)
        if (countdownRef.current) clearInterval(countdownRef.current)
        countdownRef.current = null
        onRateLimitExpired?.()
        return
      }
      setRemainingMs(diff)
    }

    tick()
    countdownRef.current = setInterval(tick, 1000)

    return () => {
      if (countdownRef.current) clearInterval(countdownRef.current)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rateLimitExpiresAt])

  const isRateLimited = remainingMs > 0

  // ── Form reset ─────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!open || !brand) {
      reset({ amountInput: '' })
      return
    }
    if (isFixedAmount) {
      reset({ amountInput: brand.minAmountUsd.toFixed(2) })
      void trigger('amountInput')
      return
    }
    reset({ amountInput: '' })
  }, [open, brand, isFixedAmount, reset, trigger])

  // Auto-truncate if more than 2 decimal places are typed
  useEffect(() => {
    if (!amountInput?.includes('.')) return
    const parts = amountInput.split('.')
    if (parts[1] && parts[1].length > 2) {
      const normalized = normalizeRedeemAmountInput(amountInput)
      if (normalized !== null) {
        setValue('amountInput', normalized.toFixed(2), { shouldValidate: true })
      }
    }
  }, [amountInput, setValue])

  const onBlurFormat = () => {
    const normalized = normalizeRedeemAmountInput(amountInput)
    if (normalized !== null) {
      setValue('amountInput', normalized.toFixed(2), { shouldValidate: true })
    }
  }

  const submit = handleSubmit((values) => {
    const amountUsd = normalizeRedeemAmountInput(values.amountInput)
    if (amountUsd === null) return
    onRedeem(amountUsd)
  })

  if (!brand) return null

  const amountError = errors.amountInput?.message
  const fixedAmountHint = buildFixedAmountHint(brand)

  return (
    <ConfirmationModalShell
      open={open}
      onClose={() => onClose()}
      disableBackdropClose
      disableEscapeKeyDown
      dialogTestId="rewards-confirm-modal"
      ariaLabelledBy="rewards-confirm-title"
      paperSx={confirmRewardModalPaperSx}
      backdropSx={confirmRewardModalBackdropSx}
    >
      <Box sx={confirmRewardModalContentSx}>
        <Box sx={confirmRewardModalHeaderSx}>
          <Typography id="rewards-confirm-title" component="h2" sx={confirmRewardModalTitleSx}>
            Confirm Your Reward
          </Typography>
          <IconButton
            aria-label="Close"
            onClick={onClose}
            data-testid="rewards-confirm-close"
            sx={confirmRewardModalCloseButtonSx}
          >
            <RiCloseLine size={24} aria-hidden />
          </IconButton>
        </Box>

        <Box sx={confirmRewardBrandStripSx}>
          {brand.logoUrl ? (
            <Box
              component="img"
              src={brand.logoUrl}
              alt=""
              sx={confirmRewardBrandLogoSx}
            />
          ) : (
            <Box sx={confirmRewardBrandLogoPlaceholderSx} aria-hidden />
          )}
          <Typography sx={confirmRewardBrandNameSx}>{brand.name}</Typography>
        </Box>

        <Box sx={confirmRewardBalanceRowSx}>
          <RiInformationLine size={14} aria-hidden />
          <Typography sx={confirmRewardBalanceTextSx}>
            Use your account balance of {formatUsd(balanceUsd)}
          </Typography>
        </Box>

        <Box component="label" htmlFor="rewards-confirm-amount">
          <Typography component="span" sx={confirmRewardAmountLabelSx}>
            Enter amount ($)
          </Typography>
          <TextField
            fullWidth
            id="rewards-confirm-amount"
            placeholder={`e.g. ${resolveEffectiveMinUsd(brand.minAmountUsd).toFixed(2)}`}
            autoComplete="off"
            inputMode="decimal"
            disabled={isFixedAmount}
            {...register('amountInput', {
              onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                // TC-12B-45: strip dollar sign and any other non-numeric, non-decimal chars on paste
                const stripped = e.target.value.replaceAll(/[^0-9.]/g, '')
                if (stripped !== e.target.value) {
                  setValue('amountInput', stripped, { shouldValidate: true })
                }
              },
            })}
            onBlur={onBlurFormat}
            error={Boolean(amountError)}
            sx={confirmRewardAmountFieldSx}
            inputProps={{ 'data-testid': 'rewards-confirm-amount' }}
          />
        </Box>

        {renderAmountHint({
          amountError,
          isFixedAmount,
          brand,
          fixedAmountHint,
        })}

        {/* TC-12B-51: rate-limit countdown */}
        {isRateLimited && (
          <Typography
            role="alert"
            sx={{ fontSize: '13px', color: '#DC2626', mt: 1, mb: 0.5, textAlign: 'center' }}
            data-testid="rewards-confirm-rate-limit-message"
          >
            {REDEEM_RATE_LIMIT_PREFIX}
            <strong>{formatCountdown(remainingMs)}</strong> before trying again.
          </Typography>
        )}

        {submissionError && !isRateLimited && (
          <Typography
            role="alert"
            sx={{ fontSize: '13px', color: '#DC2626', mt: 1, mb: 0.5, textAlign: 'center' }}
            data-testid="rewards-confirm-submit-error"
          >
            {submissionError}
          </Typography>
        )}

        <Button
          fullWidth
          variant="contained"
          disabled={!isValid || isSubmitting || isRateLimited}
          onClick={() => void submit()}
          data-testid="rewards-confirm-redeem"
          sx={confirmRewardRedeemButtonSx}
        >
          {isSubmitting ? <CircularProgress size={24} color="inherit" /> : 'Redeem'}
        </Button>

        <Button
          fullWidth
          variant="text"
          onClick={onClose}
          data-testid="rewards-confirm-cancel"
          sx={confirmRewardCancelButtonSx}
        >
          Cancel
        </Button>
      </Box>
    </ConfirmationModalShell>
  )
}
